import { create } from 'zustand';
import { Player } from '@/types/cricket';
import { 
  fetchPlayers,
  createPlayer,
  updatePlayer as updatePlayerAPI,
  deletePlayer as deletePlayerAPI,
  getPlayer as getPlayerAPI
} from '@/services/supabaseService';
import { toast } from '@/components/ui/use-toast';

interface PlayerStore {
  players: Player[];
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchPlayers: () => Promise<void>;
  addPlayer: (player: Omit<Player, 'id'> & { profilePictureFile?: File | null, coverImageFile?: File | null }) => Promise<void>;
  updatePlayer: (playerId: string, player: Partial<Player>) => Promise<void>;
  deletePlayer: (playerId: string) => Promise<void>;
  getPlayer: (playerId: string) => Promise<Player | null>;
}

// Only mid/dark, vibrant colors (no light, white, black, or grays)
const playerAvatarColors = [
  '#E65100', // deep orange
  '#F57C00', // orange
  '#C62828', // red
  '#AD1457', // pink
  '#6A1B9A', // purple
  '#283593', // indigo
  '#1565C0', // blue
  '#0277BD', // cyan
  '#00897B', // teal
  '#2E7D32', // green
  '#558B2F', // dark lime
  '#F9A825', // gold
  '#FF8F00', // amber
  '#D84315', // brownish orange
  '#4E342E', // brown
  '#4527A0', // deep purple
  '#1A237E', // navy
  '#0D47A1', // dark blue
  '#00695C', // dark teal
  '#33691E', // olive
  '#BF360C', // burnt orange
  '#B71C1C', // dark red
  '#880E4F', // deep pink
  '#4A148C', // deep purple
  '#01579B', // blue
  '#006064', // teal
  '#1B5E20', // dark green
  '#F57F17', // dark yellow
  '#FF6F00', // orange
  '#3E2723', // dark brown
];

export const getRandomColor = () => {
  return playerAvatarColors[Math.floor(Math.random() * playerAvatarColors.length)];
};

const usePlayerStore = create<PlayerStore>((set, get) => ({
  players: [],
  loading: false,
  error: null,
  
  fetchPlayers: async () => {
    set({ loading: true, error: null });
    try {
      const playersData = await fetchPlayers();
      set({ players: playersData, loading: false });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error fetching players:", error);
      set({ 
        error: message || "Failed to load players", 
        loading: false 
      });
      toast({
        title: "Error",
        description: "Failed to load players",
        variant: "destructive",
      });
    }
  },
  
  addPlayer: async (player) => {
    try {
      const newPlayer = await createPlayer(player);
      set((state) => ({
        players: [newPlayer, ...state.players] 
      }));
      toast({
        title: 'Success',
        description: `${newPlayer.name} added successfully!`,
      });
    } catch (error) {
      console.error('Error creating player:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create player',
        variant: 'destructive',
      });
      throw error;
    }
  },

  updatePlayer: async (playerId, player) => {
    try {
      const updatedPlayer = await updatePlayerAPI({ id: playerId, ...player });
      set((state) => ({
        players: state.players.map((p) =>
          p.id === playerId ? updatedPlayer : p
        ),
      }));
      toast({
        title: 'Success',
        description: `${updatedPlayer.name} updated successfully!`,
      });
    } catch (error) {
      console.error('Error updating player:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update player',
        variant: 'destructive',
      });
      throw error;
    }
  },

  deletePlayer: async (playerId) => {
    try {
      // Find player name before deleting
      const playerName = get().players.find((p) => p.id === playerId)?.name || 'Player';
      await deletePlayerAPI(playerId);
      set((state) => ({
        players: state.players.filter((player) => player.id !== playerId),
      }));
      toast({
        title: 'Deleted',
        description: `${playerName} deleted successfully!`,
        variant: 'destructive',
      });
    } catch (error) {
      console.error('Error deleting player:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete player',
        variant: 'destructive',
      });
      throw error;
    }
  },

  getPlayer: async (playerId) => {
    try {
      const player = await getPlayerAPI(playerId);
      return player;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error fetching player:", error);
      toast({
        title: "Error",
        description: message || "Failed to fetch player",
        variant: "destructive",
      });
      return null;
    }
  },
}));

export default usePlayerStore; 